import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  createAdminEmailTemplate,
  deleteAdminEmailTemplate,
  duplicateAdminEmailTemplate,
  fetchAdminEmailTemplate,
  fetchAdminEmailTemplates,
  fetchEmailPortalHistory,
  previewAdminEmailTemplate,
  previewEmailPortalEmail,
  sendEmailPortalEmail,
  updateAdminEmailTemplate,
  type AdminEmailPreview,
  type AdminEmailTemplateInput,
  type AdminEmailTemplateSummary,
  type BetaTester,
  type EmailPortalHistoryResponse,
  type EmailPortalSendResponse,
} from '../../../lib/adminTools';

const DEFAULT_PORTAL_MESSAGE = 'I’m inviting you to try Trade-Proof and see what your trading history can teach you.\n\nI’d love to hear what you think as we continue improving the platform.';
const BetaTestersSection = lazy(() => import('./BetaTestersSection'));

type Identity = { uid: string; email?: string | null } | null;
type Theme = 'light' | 'dark';
type TemplateInput = AdminEmailTemplateInput;
type TemplateColumnSizes = { templates: number; editor: number; preview: number };
type ColumnDivider = 1 | 2;
type BetaBatchSendOutcome = { status: 'success' | 'partial' | 'failed' | 'error'; sentCount: number; totalCount: number; message: string };

const DEFAULT_TEMPLATE_COLUMN_SIZES: TemplateColumnSizes = { templates: 25, editor: 35, preview: 40 };
const TEMPLATE_COLUMN_STORAGE_KEY = 'trade-proof.admin.email-template-columns.v1';
const TEMPLATE_COLUMN_MIN_WIDTHS = [224, 340, 360] as const;
const TEMPLATE_DIVIDER_WIDTH = 8;

const normalizeTemplateColumnSizes = (value: unknown): TemplateColumnSizes => {
  if (!value || typeof value !== 'object') return DEFAULT_TEMPLATE_COLUMN_SIZES;
  const candidate = value as Partial<TemplateColumnSizes>;
  const values = [candidate.templates, candidate.editor, candidate.preview];
  if (values.some((item) => typeof item !== 'number' || !Number.isFinite(item) || item <= 0)) return DEFAULT_TEMPLATE_COLUMN_SIZES;
  const total = values.reduce<number>((sum, item) => sum + (item as number), 0);
  if (!Number.isFinite(total) || total <= 0) return DEFAULT_TEMPLATE_COLUMN_SIZES;
  const [templates, editor, preview] = values.map((item) => (item as number) * 100 / total);
  return templates >= 0.1 && editor >= 0.1 && preview >= 0.1
    ? { templates, editor, preview }
    : DEFAULT_TEMPLATE_COLUMN_SIZES;
};

const readTemplateColumnSizes = (): TemplateColumnSizes => {
  try {
    const saved = window.localStorage.getItem(TEMPLATE_COLUMN_STORAGE_KEY);
    return saved ? normalizeTemplateColumnSizes(JSON.parse(saved)) : DEFAULT_TEMPLATE_COLUMN_SIZES;
  } catch {
    return DEFAULT_TEMPLATE_COLUMN_SIZES;
  }
};

const saveTemplateColumnSizes = (sizes: TemplateColumnSizes) => {
  try { window.localStorage.setItem(TEMPLATE_COLUMN_STORAGE_KEY, JSON.stringify(sizes)); } catch { /* Storage is optional. */ }
};

const serializeTemplate = (template: TemplateInput) => JSON.stringify(template);

const newTemplate = (): TemplateInput => ({
  name: '',
  subject: '',
  htmlContent: '',
  textContent: 'Hi {{first_name}},\n\n{{personal_message}}\n\n{{invitation_url}}',
  defaultPersonalMessage: DEFAULT_PORTAL_MESSAGE,
  evaluationType: null,
});

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function EmailPortalSection({ theme, identity }: { theme: Theme; identity: Identity }) {
  const dark = theme === 'dark';

  // Composer state
  const [templates, setTemplates] = useState<AdminEmailTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [isSubjectUserEdited, setIsSubjectUserEdited] = useState(false);
  const [isMessageUserEdited, setIsMessageUserEdited] = useState(false);

  // History state
  const [history, setHistory] = useState<EmailPortalHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Preview & sending state
  const [preview, setPreview] = useState<AdminEmailPreview | null>(null);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState<EmailPortalSendResponse | null>(null);
  const [selectedBetaTesters, setSelectedBetaTesters] = useState<BetaTester[]>([]);
  const [betaBatchPreviews, setBetaBatchPreviews] = useState<Array<{ tester: BetaTester; preview: AdminEmailPreview }>>([]);
  const [betaBatchPreviewSignature, setBetaBatchPreviewSignature] = useState('');
  const [betaBatchSummary, setBetaBatchSummary] = useState('');

  // Template Manager Workspace State
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [htmlEditorExpanded, setHtmlEditorExpanded] = useState(true);
  const [plainTextExpanded, setPlainTextExpanded] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [templateVersionHash, setTemplateVersionHash] = useState('');
  const [templateNameEditing, setTemplateNameEditing] = useState(false);
  const [templateForm, setTemplateForm] = useState<TemplateInput>(newTemplate());
  const [savedTemplateForm, setSavedTemplateForm] = useState<TemplateInput>(newTemplate());
  const [templatePreview, setTemplatePreview] = useState<AdminEmailPreview | null>(null);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateNotice, setTemplateNotice] = useState('');
  const [templateColumnSizes, setTemplateColumnSizes] = useState<TemplateColumnSizes>(readTemplateColumnSizes);
  const [isWideTemplateWorkspace, setIsWideTemplateWorkspace] = useState(() => (
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1280px)').matches
  ));
  const [activeColumnDivider, setActiveColumnDivider] = useState<ColumnDivider | null>(null);
  const [betaTestersViewOpen, setBetaTestersViewOpen] = useState(false);

  const templatePreviewRequestIdRef = useRef(0);
  const templateColumnsRef = useRef<HTMLDivElement>(null);
  const templateColumnSizesRef = useRef(templateColumnSizes);
  const columnDragRef = useRef<{ divider: ColumnDivider; pointerId: number; startX: number; startWidths: number[] } | null>(null);
  const previousBodyDragStylesRef = useRef<{ userSelect: string; cursor: string } | null>(null);

  const selectedTemplate = useMemo(() => (
    templates.find((t) => t.id === selectedTemplateId) || null
  ), [templates, selectedTemplateId]);
  const betaBatchSignature = JSON.stringify({
    testers: selectedBetaTesters.map(({ id, email, name }) => ({ id, email, name })),
    templateId: selectedTemplateId, subject, personalMessage,
  });

  const updateBetaRecipients = (next: BetaTester[]) => {
    setSelectedBetaTesters(next);
    setBetaBatchPreviews([]); setBetaBatchPreviewSignature(''); setBetaBatchSummary(''); setPreview(null); setSendConfirmation(null);
  };

  const updateSubject = (next: string) => {
    setSubject(next);
    setIsSubjectUserEdited(true);
    setPreview(null);
    setBetaBatchPreviews([]);
    setBetaBatchPreviewSignature('');
    setBetaBatchSummary('');
    setSendConfirmation(null);
  };

  const updateSelectedTemplate = (next: string) => {
    setSelectedTemplateId(next);
    setIsSubjectUserEdited(false);
    setIsMessageUserEdited(false);
    setPreview(null);
    setBetaBatchPreviews([]);
    setBetaBatchPreviewSignature('');
    setBetaBatchSummary('');
    setSendConfirmation(null);
  };

  const editingTemplateSummary = useMemo(() => (
    templates.find((t) => t.id === templateId) || null
  ), [templates, templateId]);

  const templateHasUnsavedChanges = serializeTemplate(templateForm) !== serializeTemplate(savedTemplateForm);

  const surface = `rounded-xl border p-4 ${dark ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-zinc-200 bg-white text-zinc-900'}`;
  const composerSurface = `rounded-xl border p-4 ${dark ? 'border-slate-700 bg-[#11151C] text-slate-100' : 'border-zinc-200 bg-white text-zinc-900'}`;
  const input = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-slate-700 bg-slate-950 text-white' : 'border-zinc-300 bg-white text-zinc-900'}`;

  // Load templates on initial mount
  const loadTemplates = useCallback(async () => {
    if (typeof fetchAdminEmailTemplates !== 'function') return;
    try {
      const templateResponse = await fetchAdminEmailTemplates(identity);
      const loaded = Array.isArray(templateResponse.templates) ? templateResponse.templates : [];
      setTemplates(loaded);
      setSelectedTemplateId((curr) => {
        if (curr && loaded.some((item) => item.id === curr)) return curr;
        return loaded.find((item) => item.systemKey === 'beta_invitation_v23')?.id || loaded[0]?.id || '';
      });
    } catch (loadErr) {
      setError(loadErr instanceof Error ? loadErr.message : 'Unable to load email templates.');
    }
  }, [identity]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  // When selected template changes, prefill subject & default message if not explicitly customized
  useEffect(() => {
    if (!selectedTemplate) return;
    if (!isSubjectUserEdited) {
      setSubject(selectedTemplate.subject);
    }
    if (!isMessageUserEdited) {
      setPersonalMessage(selectedTemplate.defaultPersonalMessage || '');
    }
    setPreview(null);
  }, [selectedTemplate, isSubjectUserEdited, isMessageUserEdited]);

  // Fetch history when recipientEmail or selectedTemplateId changes
  useEffect(() => {
    const trimmed = recipientEmail.trim();
    if (selectedBetaTesters.length || !isValidEmail(trimmed)) {
      setHistory(null);
      return undefined;
    }

    let active = true;
    if (typeof fetchEmailPortalHistory !== 'function') return undefined;
    setHistoryLoading(true);
    const timeout = setTimeout(() => {
      fetchEmailPortalHistory(identity, trimmed, selectedTemplateId)
        .then((res) => {
          if (active) setHistory(res);
        })
        .catch(() => {
          if (active) setHistory(null);
        })
        .finally(() => {
          if (active) setHistoryLoading(false);
        });
    }, 350);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [identity, recipientEmail, selectedTemplateId, selectedBetaTesters.length]);

  // Handle template manager column resizing and cleanup
  useEffect(() => {
    if (!templateManagerOpen || !templateHasUnsavedChanges) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [templateHasUnsavedChanges, templateManagerOpen]);

  const confirmDiscardTemplateChanges = () => (
    !templateHasUnsavedChanges || window.confirm('Discard unsaved email template changes?')
  );

  const applyTemplateColumnSizes = (sizes: TemplateColumnSizes, persist = false) => {
    const normalized = normalizeTemplateColumnSizes(sizes);
    templateColumnSizesRef.current = normalized;
    setTemplateColumnSizes(normalized);
    if (persist) saveTemplateColumnSizes(normalized);
  };

  const getTemplateColumnWidths = () => {
    const root = templateColumnsRef.current;
    if (!root) return null;
    const columns = Array.from(root.querySelectorAll<HTMLElement>('[data-template-column]'));
    if (columns.length !== 3) return null;
    const widths = columns.map((column) => column.getBoundingClientRect().width);
    return widths.every((width) => Number.isFinite(width) && width > 0) ? widths : null;
  };

  const resizeTemplateColumnsBy = (divider: ColumnDivider, deltaX: number, startWidths = getTemplateColumnWidths(), persist = false) => {
    if (!startWidths) return;
    const leftIndex = divider - 1;
    const rightIndex = divider;
    const pairWidth = startWidths[leftIndex] + startWidths[rightIndex];
    const minLeft = TEMPLATE_COLUMN_MIN_WIDTHS[leftIndex];
    const minRight = TEMPLATE_COLUMN_MIN_WIDTHS[rightIndex];
    const nextLeft = Math.max(minLeft, Math.min(pairWidth - minRight, startWidths[leftIndex] + deltaX));
    const nextWidths = [...startWidths];
    nextWidths[leftIndex] = nextLeft;
    nextWidths[rightIndex] = pairWidth - nextLeft;
    const total = nextWidths.reduce((sum, width) => sum + width, 0);
    if (total <= 0) return;
    applyTemplateColumnSizes({
      templates: nextWidths[0] * 100 / total,
      editor: nextWidths[1] * 100 / total,
      preview: nextWidths[2] * 100 / total,
    }, persist);
  };

  const finishTemplateColumnDrag = () => {
    if (!columnDragRef.current) return;
    columnDragRef.current = null;
    setActiveColumnDivider(null);
    saveTemplateColumnSizes(templateColumnSizesRef.current);
    if (previousBodyDragStylesRef.current && typeof document !== 'undefined') {
      document.body.style.userSelect = previousBodyDragStylesRef.current.userSelect;
      document.body.style.cursor = previousBodyDragStylesRef.current.cursor;
      previousBodyDragStylesRef.current = null;
    }
  };

  const startTemplateColumnDrag = (divider: ColumnDivider, event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isWideTemplateWorkspace) return;
    const widths = getTemplateColumnWidths();
    if (!widths) return;
    event.preventDefault();
    columnDragRef.current = { divider, pointerId: event.pointerId, startX: event.clientX, startWidths: widths };
    previousBodyDragStylesRef.current = { userSelect: document.body.style.userSelect, cursor: document.body.style.cursor };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    setActiveColumnDivider(divider);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Optional in older browsers */ }
  };

  const moveTemplateColumnDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = columnDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    resizeTemplateColumnsBy(drag.divider, event.clientX - drag.startX, drag.startWidths);
  };

  const nudgeTemplateColumnDivider = (divider: ColumnDivider, direction: -1 | 1) => {
    resizeTemplateColumnsBy(divider, direction * 24, getTemplateColumnWidths(), true);
  };

  const resetTemplateColumnSizes = () => applyTemplateColumnSizes(DEFAULT_TEMPLATE_COLUMN_SIZES, true);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(min-width: 1280px)');
    const updateWideMode = () => {
      finishTemplateColumnDrag();
      setIsWideTemplateWorkspace(media.matches);
    };
    updateWideMode();
    media.addEventListener?.('change', updateWideMode);
    window.addEventListener('resize', updateWideMode);
    return () => {
      media.removeEventListener?.('change', updateWideMode);
      window.removeEventListener('resize', updateWideMode);
      finishTemplateColumnDrag();
    };
  }, []);

  // Action: Preview Email
  const handlePreview = async () => {
    if (!selectedTemplateId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (selectedBetaTesters.length) {
        const rendered = await Promise.all(selectedBetaTesters.map(async (tester) => {
          const result = await previewEmailPortalEmail(identity, {
            templateId: selectedTemplateId,
            recipientEmail: tester.email,
            recipientName: tester.name,
            subject: subject.trim() || undefined,
            personalMessage,
          });
          return { tester, preview: result.preview };
        }));
        setBetaBatchPreviews(rendered);
        setBetaBatchPreviewSignature(betaBatchSignature);
        setPreview(null);
        setNotice(`Rendered ${rendered.length} personalized previews. No email was sent.`);
        return;
      }
      const result = await previewEmailPortalEmail(identity, {
        templateId: selectedTemplateId,
        recipientEmail: recipientEmail.trim() || undefined,
        recipientName: recipientName.trim() || undefined,
        subject: subject.trim() || undefined,
        personalMessage: personalMessage,
      });
      setPreview(result.preview);
      setNotice('Email preview rendered. Previewing never sends email.');
    } catch (prevErr) {
      setError(prevErr instanceof Error ? prevErr.message : 'Unable to preview email.');
    } finally {
      setBusy(false);
    }
  };

  // Action: Send Email
  const handleSend = async (skipBatchConfirmation = false): Promise<BetaBatchSendOutcome | null | undefined> => {
    if (selectedBetaTesters.length) {
      if (!selectedTemplateId || !subject.trim()) {
        const message = !selectedTemplateId ? 'Please select an email template.' : 'Subject line is required.';
        setError(message);
        return { status: 'error', sentCount: 0, totalCount: selectedBetaTesters.length, message };
      }
      if (!skipBatchConfirmation && !window.confirm(`Send ${selectedBetaTesters.length} separate “${selectedTemplate?.name || 'selected'}” emails to the selected beta testers? Each recipient receives an individual message.`)) return null;
      setSending(true); setBusy(true); setError(''); setNotice(''); setBetaBatchSummary('');
      const results: Array<{ tester: BetaTester; error?: string }> = [];
      for (const tester of selectedBetaTesters) {
        try {
          await sendEmailPortalEmail(identity, {
            templateId: selectedTemplateId,
            recipientEmail: tester.email,
            recipientName: tester.name,
            betaTesterId: tester.id,
            subject: subject.trim(),
            personalMessage,
            idempotencyKey: `send-${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${tester.id}`,
          });
          results.push({ tester });
        } catch (sendErr) {
          results.push({ tester, error: sendErr instanceof Error ? sendErr.message : 'Send failed.' });
        }
      }
      const failed = results.filter((result) => result.error);
      const succeeded = results.length - failed.length;
      const message = `${succeeded} of ${results.length} separate emails accepted by the provider.${failed.length ? ` Failed: ${failed.map(({ tester, error: reason }) => `${tester.email} (${reason})`).join('; ')}` : ''}`;
      setBetaBatchSummary(message);
      setSelectedBetaTesters(failed.map(({ tester }) => tester));
      setBetaBatchPreviews([]); setBetaBatchPreviewSignature(''); setPreview(null); setSendConfirmation(null);
      setSending(false); setBusy(false);
      return { status: failed.length === 0 ? 'success' : succeeded > 0 ? 'partial' : 'failed', sentCount: succeeded, totalCount: results.length, message };
    }
    const trimmedEmail = recipientEmail.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setError('Please enter a valid recipient email address.');
      return;
    }
    if (!selectedTemplateId) {
      setError('Please select an email template.');
      return;
    }
    if (!subject.trim()) {
      setError('Subject line is required.');
      return;
    }

    const confirmMsg = history?.previouslySent
      ? `This email was previously sent to ${trimmedEmail}. Send again now?`
      : `Send “${selectedTemplate?.name || 'selected'}” email to ${trimmedEmail}?`;

    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    setBusy(true);
    setError('');
    setNotice('');
    setSendConfirmation(null);

    const idempotencyKey = `send-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    try {
      const response = await sendEmailPortalEmail(identity, {
        templateId: selectedTemplateId,
        recipientEmail: trimmedEmail,
        recipientName: recipientName.trim() || undefined,
        subject: subject.trim(),
        personalMessage: personalMessage,
        idempotencyKey,
      });

      setSendConfirmation(response);
      setNotice('Email sent successfully.');
      setPreview(null);

      // Refresh history
      void fetchEmailPortalHistory(identity, trimmedEmail, selectedTemplateId).then(setHistory).catch(() => undefined);
    } catch (sendErr) {
      setError(sendErr instanceof Error ? sendErr.message : 'Unable to send email.');
    } finally {
      setSending(false);
      setBusy(false);
    }
  };

  // Template Manager Handlers
  const openTemplateManager = async () => {
    setTemplateManagerOpen(true);
    setTemplatePreview(null);
    setTemplateError('');
    setTemplateNotice('');
    const preferredId = selectedTemplateId || templates[0]?.id;
    if (!preferredId) {
      templatePreviewRequestIdRef.current += 1;
      setTemplateId('');
      setTemplateVersionHash('');
      setTemplateNameEditing(true);
      setTemplateForm(newTemplate());
      setTemplatePreviewLoading(false);
      return;
    }
    await loadTemplate(preferredId);
  };

  const loadTemplate = async (id: string) => {
    if (!confirmDiscardTemplateChanges()) return;
    const requestId = ++templatePreviewRequestIdRef.current;
    setTemplateError('');
    setTemplateNotice('');
    setTemplatePreview(null);
    setTemplatePreviewLoading(true);
    try {
      const result = await fetchAdminEmailTemplate(identity, id);
      if (templatePreviewRequestIdRef.current !== requestId) return;
      setTemplateId(result.template.id);
      setTemplateVersionHash(result.template.versionHash);
      setTemplateNameEditing(false);
      const nextTemplate = {
        name: result.template.name,
        subject: result.template.subject,
        htmlContent: result.template.htmlContent,
        textContent: result.template.textContent,
        defaultPersonalMessage: result.template.defaultPersonalMessage,
        evaluationType: result.template.evaluationType,
      };
      setTemplateForm(nextTemplate);
      setSavedTemplateForm(nextTemplate);

      try {
        const previewResult = await previewAdminEmailTemplate(identity, {
          template: nextTemplate,
          name: recipientName.trim() || 'there',
          personalMessage: nextTemplate.defaultPersonalMessage,
        });
        if (templatePreviewRequestIdRef.current === requestId) {
          setTemplatePreview(previewResult.preview);
        }
      } catch (prevErr) {
        if (templatePreviewRequestIdRef.current === requestId) {
          setTemplateError(prevErr instanceof Error ? prevErr.message : 'Unable to render template preview.');
        }
      }
    } catch (loadErr) {
      if (templatePreviewRequestIdRef.current === requestId) {
        setTemplateError(loadErr instanceof Error ? loadErr.message : 'Unable to load template.');
      }
    } finally {
      if (templatePreviewRequestIdRef.current === requestId) {
        setTemplatePreviewLoading(false);
      }
    }
  };

  const startNewTemplate = () => {
    if (!confirmDiscardTemplateChanges()) return;
    templatePreviewRequestIdRef.current += 1;
    const blank = newTemplate();
    setTemplateId('');
    setTemplateVersionHash('');
    setTemplateNameEditing(true);
    setTemplateForm(blank);
    setSavedTemplateForm(blank);
    setTemplatePreview(null);
    setTemplatePreviewLoading(false);
    setTemplateError('');
    setTemplateNotice('');
  };

  const saveTemplate = async () => {
    setBusy(true);
    setTemplateError('');
    setTemplateNotice('');
    try {
      const result = templateId
        ? await updateAdminEmailTemplate(identity, templateId, templateForm, templateVersionHash)
        : await createAdminEmailTemplate(identity, templateForm);
      const saved = {
        name: result.template.name,
        subject: result.template.subject,
        htmlContent: result.template.htmlContent,
        textContent: result.template.textContent,
        defaultPersonalMessage: result.template.defaultPersonalMessage,
        evaluationType: result.template.evaluationType,
      };
      setTemplateId(result.template.id);
      setTemplateVersionHash(result.template.versionHash);
      setTemplateForm(saved);
      setSavedTemplateForm(saved);
      setTemplateNameEditing(false);
      const updated = await fetchAdminEmailTemplates(identity);
      setTemplates(updated.templates);
      setTemplateNotice('Template saved successfully.');
    } catch (saveErr) {
      setTemplateError(saveErr instanceof Error ? saveErr.message : 'Unable to save template.');
    } finally {
      setBusy(false);
    }
  };

  const duplicateTemplate = async () => {
    if (!templateId) return;
    setBusy(true);
    setTemplateError('');
    setTemplateNotice('');
    try {
      const result = await duplicateAdminEmailTemplate(identity, templateId);
      const updated = await fetchAdminEmailTemplates(identity);
      setTemplates(updated.templates);
      await loadTemplate(result.template.id);
      setTemplateNotice('Template duplicated. Edit the copy and save when ready.');
    } catch (dupErr) {
      setTemplateError(dupErr instanceof Error ? dupErr.message : 'Unable to duplicate template.');
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!templateId || !window.confirm(`Delete “${templateForm.name}”?`)) return;
    templatePreviewRequestIdRef.current += 1;
    setBusy(true);
    setTemplateError('');
    setTemplateNotice('');
    try {
      await deleteAdminEmailTemplate(identity, templateId);
      const updated = await fetchAdminEmailTemplates(identity);
      setTemplates(updated.templates);
      const blank = newTemplate();
      setTemplateId('');
      setTemplateVersionHash('');
      setTemplateNameEditing(true);
      setTemplateForm(blank);
      setSavedTemplateForm(blank);
      setTemplatePreview(null);
      setTemplatePreviewLoading(false);
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(updated.templates[0]?.id || '');
      }
      setTemplateNotice('Template deleted.');
    } catch (delErr) {
      setTemplateError(delErr instanceof Error ? delErr.message : 'Unable to delete template.');
    } finally {
      setBusy(false);
    }
  };

  const previewTemplateDraft = async () => {
    const requestId = ++templatePreviewRequestIdRef.current;
    setBusy(true);
    setTemplatePreviewLoading(true);
    setTemplateError('');
    setTemplateNotice('');
    try {
      const result = await previewAdminEmailTemplate(identity, {
        template: templateForm,
        name: recipientName.trim() || 'there',
        personalMessage: templateForm.defaultPersonalMessage,
      });
      if (templatePreviewRequestIdRef.current === requestId) {
        setTemplatePreview(result.preview);
        setTemplateNotice('Personalized template preview ready. No email was sent.');
      }
    } catch (prevErr) {
      if (templatePreviewRequestIdRef.current === requestId) {
        setTemplateError(prevErr instanceof Error ? prevErr.message : 'Unable to render template preview.');
      }
    } finally {
      if (templatePreviewRequestIdRef.current === requestId) {
        setTemplatePreviewLoading(false);
        setBusy(false);
      }
    }
  };

  const selectTemplateForComposer = () => {
    if (!templateId || !templates.some((item) => item.id === templateId)) return;
    if (!confirmDiscardTemplateChanges()) return;
    templatePreviewRequestIdRef.current += 1;
    setTemplateForm(savedTemplateForm);
    setSelectedTemplateId(templateId);
    setIsSubjectUserEdited(false);
    setIsMessageUserEdited(false);
    setPreview(null);
    setTemplatePreview(null);
    setTemplatePreviewLoading(false);
    setTemplateManagerOpen(false);
    setNotice(`“${templates.find((item) => item.id === templateId)?.name || templateForm.name}” selected for the Email Portal.`);
  };

  const setFormValue = (key: keyof TemplateInput, value: string) => setTemplateForm((curr) => ({ ...curr, [key]: value }));

  const leaveTemplateManager = () => {
    if (!confirmDiscardTemplateChanges()) return;
    templatePreviewRequestIdRef.current += 1;
    setTemplateForm(savedTemplateForm);
    setTemplatePreview(null);
    setTemplatePreviewLoading(false);
    setTemplateManagerOpen(false);
  };

  const renderPreview = (value: AdminEmailPreview, title: string, showSizeToggle = true, fillHeight = false) => (
    <div
      data-testid={fillHeight ? 'email-template-preview-card' : undefined}
      className={`${fillHeight ? 'flex min-h-0 flex-1 flex-col' : 'mt-4'} overflow-hidden rounded-xl border ${dark ? 'border-slate-700' : 'border-zinc-300'}`}
    >
      {showSizeToggle ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-inherit px-3 py-2">
          <strong className="text-xs">{title}</strong>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPreviewWidth('desktop')}
              aria-pressed={previewWidth === 'desktop'}
              className={`rounded border px-2 py-1 text-xs ${previewWidth === 'desktop' ? (dark ? 'bg-slate-800 text-white' : 'bg-blue-600 text-white') : ''}`}
            >
              Desktop
            </button>
            <button
              type="button"
              onClick={() => setPreviewWidth('mobile')}
              aria-pressed={previewWidth === 'mobile'}
              className={`rounded border px-2 py-1 text-xs ${previewWidth === 'mobile' ? (dark ? 'bg-slate-800 text-white' : 'bg-blue-600 text-white') : ''}`}
            >
              Mobile
            </button>
          </div>
        </div>
      ) : null}
      <div className="space-y-1 border-b border-inherit px-3 py-2 text-xs">
        <div><b>Subject:</b> {value.subject}</div>
        {value.recipient ? <div><b>To:</b> {value.recipient}</div> : null}
      </div>
      <div
        data-testid={fillHeight ? 'email-template-preview-frame-area' : undefined}
        className={`overflow-auto p-2 ${fillHeight ? 'min-h-0 flex-1' : ''} ${dark ? 'bg-slate-800' : 'bg-zinc-200'}`}
        style={fillHeight ? undefined : { height: previewWidth === 'mobile' ? 700 : 650 }}
      >
        <iframe
          title={`${title} ${previewWidth} preview`}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={value.html}
          className="mx-auto block shrink-0 bg-white"
          style={{ width: previewWidth === 'mobile' ? 375 : 600, height: fillHeight ? '100%' : previewWidth === 'mobile' ? 680 : 630, border: 0 }}
        />
      </div>
      <details
        data-testid={fillHeight ? 'email-template-preview-text-fallback' : undefined}
        className={`border-t border-inherit px-3 py-2 text-xs ${fillHeight ? 'flex h-[49px] shrink-0 flex-col-reverse gap-3 open:h-auto' : ''}`}
      >
        <summary className={`cursor-pointer font-semibold ${fillHeight ? 'w-fit rounded-md bg-[#0A3F8D] px-3 py-2 text-white transition-colors hover:bg-[#0B4AA5]' : ''}`}>
          Plain-text fallback
        </summary>
        <pre className={`mt-2 whitespace-pre-wrap font-sans ${fillHeight ? 'max-h-48 overflow-y-auto' : ''}`}>
          {value.text}
        </pre>
      </details>
    </div>
  );

  const renderTemplateColumnDivider = (divider: ColumnDivider, label: string) => isWideTemplateWorkspace ? (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(divider === 1 ? templateColumnSizes.templates : templateColumnSizes.templates + templateColumnSizes.editor)}
      aria-valuemin={1}
      aria-valuemax={99}
      tabIndex={0}
      data-testid={`template-column-divider-${divider}`}
      onPointerDown={(event) => startTemplateColumnDrag(divider, event)}
      onPointerMove={moveTemplateColumnDrag}
      onPointerUp={finishTemplateColumnDrag}
      onPointerCancel={finishTemplateColumnDrag}
      onLostPointerCapture={finishTemplateColumnDrag}
      onDoubleClick={resetTemplateColumnSizes}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); nudgeTemplateColumnDivider(divider, -1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); nudgeTemplateColumnDivider(divider, 1); }
        if (event.key === 'Home') { event.preventDefault(); resetTemplateColumnSizes(); }
      }}
      className={`group relative z-10 flex min-w-0 touch-none cursor-col-resize items-center justify-center outline-none transition-colors ${dark ? 'bg-slate-950 hover:bg-blue-500/10 focus-visible:bg-blue-500/10' : 'bg-zinc-100 hover:bg-blue-500/10 focus-visible:bg-blue-500/10'} ${activeColumnDivider === divider ? 'select-none bg-blue-500/15' : ''}`}
      style={{ width: TEMPLATE_DIVIDER_WIDTH, userSelect: 'none' }}
    >
      <span aria-hidden="true" className={`h-full w-px transition-colors ${activeColumnDivider === divider ? 'bg-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.55)]' : dark ? 'bg-slate-800 group-hover:bg-blue-400' : 'bg-zinc-300 group-hover:bg-blue-500'}`} />
    </div>
  ) : null;

  if (betaTestersViewOpen) {
    return (
      <div className="md:col-span-2 min-w-0" data-testid="admin-email-portal">
        <Suspense fallback={<div role="status" className="rounded-xl border border-slate-700 bg-slate-900 p-5 text-sm text-slate-300">Loading Beta Testers…</div>}>
          <BetaTestersSection
            theme={theme}
            identity={identity}
            subject={subject}
            onSubjectChange={updateSubject}
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onTemplateChange={updateSelectedTemplate}
            onSendMessage={(skipBrowserConfirmation) => handleSend(skipBrowserConfirmation)}
            sending={sending}
            selectedTesters={selectedBetaTesters}
            onSelectedTestersChange={updateBetaRecipients}
            onBackToEmailPortal={() => setBetaTestersViewOpen(false)}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <section className={`md:col-span-2 rounded-2xl border p-5 ${dark ? 'border-slate-800 bg-slate-950/50 text-slate-100' : 'border-zinc-200 bg-white text-zinc-900'}`} data-testid="admin-email-portal">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">EMAIL PORTAL</h2>
          <p className="mt-1 text-sm opacity-70">Compose, preview, and send emails using saved templates.</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          <button
            type="button"
            onClick={() => void openTemplateManager()}
            className="group relative inline-flex min-h-10 cursor-pointer select-none items-center gap-2.5 rounded-xl border border-slate-700/80 bg-[#0F172A] px-3.5 py-1.5 text-sm font-medium text-slate-100 backdrop-blur-sm transition-all duration-200 hover:border-teal-500/50 hover:bg-slate-800/90 hover:text-white hover:shadow-[0_4px_16px_rgba(20,184,166,0.15)] active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-teal-500/10 text-teal-400 transition-all duration-200 group-hover:scale-105 group-hover:bg-teal-500/20">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            </span>
            <span className="font-medium text-slate-200 transition-colors group-hover:text-white">Email Templates</span>
            <span className="sr-only"> · </span>
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-teal-500/40 bg-teal-500/20 px-1.5 font-mono text-xs font-semibold text-teal-300 transition-all group-hover:border-teal-400 group-hover:bg-teal-500/25">
              {templates.length}
            </span>
          </button>
          <button
            type="button"
            data-testid="email-portal-beta-testers-toggle"
            onClick={() => setBetaTestersViewOpen(true)}
            className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-700/80 bg-[#0F172A] px-3.5 py-2 text-sm font-semibold text-slate-100 transition-colors hover:border-teal-500/50 hover:bg-slate-800"
          >
            Beta Testers
          </button>
        </div>
      </div>

      {/* Main Composer Layout */}
      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-[minmax(18rem,0.9fr)_minmax(22rem,1.1fr)]">
        {/* Left Column: Recipient & Template Config */}
        <div className={composerSurface}>
          <h3 className="font-semibold text-sm text-[#61E307]">RECIPIENT &amp; TEMPLATE</h3>

          {selectedBetaTesters.length ? (
            <div className={`mt-3 rounded-lg border p-3 ${dark ? 'border-teal-500/40 bg-teal-500/10' : 'border-teal-300 bg-teal-50'}`} data-testid="email-portal-beta-recipients">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><div className="text-sm font-semibold">{selectedBetaTesters.length} beta tester{selectedBetaTesters.length === 1 ? '' : 's'} selected</div><div className="mt-1 text-xs opacity-75">Each gets a separate email personalized with their name.</div></div>
                <button type="button" onClick={() => updateBetaRecipients([])} className="rounded-lg border border-inherit px-3 py-1.5 text-xs font-semibold">Clear recipients</button>
              </div>
              <ul className="mt-2 max-h-28 space-y-1 overflow-auto text-xs">{selectedBetaTesters.map((tester) => <li key={tester.id} className="truncate">{tester.name} · {tester.email}</li>)}</ul>
            </div>
          ) : <>
          <label className="mt-3 block text-sm font-medium">
            Recipient email
            <input
              aria-label="Recipient email"
              type="email"
              placeholder="trader@example.com"
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e.target.value);
                setPreview(null);
                setSendConfirmation(null);
              }}
              maxLength={320}
              className={input}
              style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}
            />
          </label>
          <label className="mt-3 block text-sm font-medium">
            Recipient name (optional)
            <input
              aria-label="Recipient name"
              placeholder="e.g. Ross Cameron"
              value={recipientName}
              onChange={(e) => {
                setRecipientName(e.target.value);
                setPreview(null);
                setSendConfirmation(null);
              }}
              maxLength={160}
              className={input}
              style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}
            />
          </label>
          </>}

          <label className="mt-3 block text-sm font-medium">
            Email template
            <span className="email-template-select-wrap">
              <select
                aria-label="Email template"
                value={selectedTemplateId}
                onChange={(e) => updateSelectedTemplate(e.target.value)}
                disabled={!templates.length}
                className={`${input} email-template-select`}
                style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}
              >
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" className="email-template-select-chevron" />
            </span>
          </label>

          {selectedTemplate?.evaluationType === 'GENERIC_PARTNERSHIP_EVALUATION' ? (
            <p className={`mt-2 rounded-lg border px-3 py-2 text-xs ${dark ? 'border-teal-500/30 bg-teal-500/10 text-teal-200' : 'border-teal-300 bg-teal-50 text-teal-900'}`}>
              Prospective contact: this recipient does not need to be an approved White-Label tenant. The secure invitation does not add or approve a tenant.
            </p>
          ) : null}

          <label className="mt-3 block text-sm font-medium">
            Subject
            <input
              aria-label="Email subject"
              placeholder="Subject line"
              value={subject}
              onChange={(e) => updateSubject(e.target.value)}
              maxLength={250}
              className={input}
              style={{ fontFamily: '"Inter Variable", Arial, sans-serif' }}
            />
          </label>

          {/* Send History Warning / Notice */}
          {historyLoading ? (
            <div className="mt-3 text-xs opacity-60">Checking send history...</div>
          ) : history?.previouslySent ? (
            <div
              data-testid="email-portal-history-notice"
              className={`mt-3 rounded-lg border p-2.5 text-xs ${
                history.matchingTemplate
                  ? dark ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-amber-400 bg-amber-50 text-amber-900'
                  : dark ? 'border-blue-500/40 bg-blue-500/10 text-blue-300' : 'border-blue-300 bg-blue-50 text-blue-900'
              }`}
            >
              {history.matchingTemplate ? (
                <span>
                  Previously sent on {new Date(history.matchingTemplate.sentAt).toLocaleString()}
                  {history.matchingTemplate.providerMessageId ? ` (ID: ${history.matchingTemplate.providerMessageId})` : ''}.
                  Sending again will deliver a fresh copy.
                </span>
              ) : history.lastSent ? (
                <span>
                  Recipient previously received “{history.lastSent.templateName}” on {new Date(history.lastSent.sentAt).toLocaleString()}.
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handlePreview()}
              disabled={busy || sending || !selectedTemplateId}
              className="rounded-lg border border-inherit bg-white px-3 py-2 text-sm font-semibold text-[#020617] disabled:opacity-50"
            >
              Preview Email
            </button>
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={busy || sending || (!selectedBetaTesters.length && !isValidEmail(recipientEmail)) || !selectedTemplateId || !subject.trim()}
              className="rounded-lg bg-[#61E307] px-4 py-2 text-sm font-semibold text-[#020617] shadow-sm transition disabled:bg-[#347605] disabled:opacity-100"
            >
              {sending ? 'Sending...' : selectedBetaTesters.length ? `Send to ${selectedBetaTesters.length} Tester${selectedBetaTesters.length === 1 ? '' : 's'}` : 'Send Email'}
            </button>
          </div>
        </div>

        {/* Right Column: Personal Message & Live Preview */}
        <div className="flex h-full min-w-0 flex-col gap-3">
          <div className={`${composerSurface} flex min-h-0 flex-1 flex-col`}>
            <label className="flex min-h-0 flex-1 flex-col text-sm font-medium">
              Personal message for recipient
              <textarea
                aria-label="Personal message"
                placeholder="Optional personal message..."
                value={personalMessage}
                onChange={(e) => {
                  setPersonalMessage(e.target.value);
                  setIsMessageUserEdited(true);
                  setPreview(null);
                  setBetaBatchSummary('');
                }}
                maxLength={3000}
                rows={4}
                className={`${input} min-h-24 flex-1 resize-y ${dark ? '[scrollbar-color:#475569_#020617] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-slate-950 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb:hover]:bg-slate-500' : ''}`}
                style={{ fontFamily: 'system-ui, sans-serif', fontWeight: 400 }}
              />
            </label>
            <p className="mt-1 text-[11px] opacity-60">
              Use **text** for blue bold formatting. Line breaks are preserved; blank lines create paragraph spacing. Supported template placeholders: {'{{first_name}}'}, {'{{personal_message}}'}, {'{{invitation_url}}'}, {'{{CTA_URL}}'}.
            </p>

            {/* Confirmation Banner */}
            {sendConfirmation ? (
              <div
                role="status"
                data-testid="email-portal-send-confirmation"
                className={`mt-4 rounded-xl border p-4 ${dark ? 'border-emerald-500/40 bg-emerald-950/30 text-emerald-200' : 'border-emerald-400 bg-emerald-50 text-emerald-900'}`}
              >
                <div className="font-semibold text-sm">Email sent successfully.</div>
                <div className="mt-2 space-y-1 text-xs opacity-90">
                  <div><b>Recipient:</b> {sendConfirmation.recipientName ? `${sendConfirmation.recipientName} <${sendConfirmation.recipientEmail}>` : sendConfirmation.recipientEmail}</div>
                  <div><b>Template:</b> {sendConfirmation.templateName}</div>
                  <div><b>Subject:</b> {sendConfirmation.subject}</div>
                  <div><b>Date:</b> {new Date(sendConfirmation.sentAt).toLocaleString()}</div>
                  {sendConfirmation.providerMessageId ? <div className="break-all"><b>Provider Message ID:</b> {sendConfirmation.providerMessageId}</div> : null}
                </div>
              </div>
            ) : null}

            {/* Preview Frame */}
            {betaBatchPreviews.length ? (
              <div className="mt-4 space-y-3" data-testid="email-portal-beta-previews">
                {betaBatchPreviews.map(({ tester, preview: testerPreview }) => <details key={tester.id} className={`rounded-lg border p-3 ${dark ? 'border-slate-700' : 'border-zinc-300'}`}>
                  <summary className="cursor-pointer text-sm font-semibold">{tester.name} · {tester.email} · {testerPreview.subject}</summary>
                  {renderPreview(testerPreview, `Email preview for ${tester.email}`)}
                </details>)}
              </div>
            ) : preview ? (
              renderPreview(preview, 'Email Portal Preview')
            ) : (
              <p className="mt-4 text-xs opacity-65">
                Click “Preview Email” to render the exact desktop and mobile view with personal messages interpolated.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error ? (
        <p role="alert" className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-500">
          {error}
        </p>
      ) : null}
      {notice && !sendConfirmation ? (
        <p role="status" className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-600">
          {notice}
        </p>
      ) : null}
      {betaBatchSummary ? <p role="status" data-testid="email-portal-beta-send-summary" className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-600">{betaBatchSummary}</p> : null}

      {/* Template Manager Modal */}
      {templateManagerOpen ? (
        <main
          data-testid="email-template-workspace"
          className={`email-template-workspace-scroll ${dark ? 'theme-dark' : 'theme-light'} fixed inset-0 z-[1100] flex h-dvh w-screen max-w-full flex-col overflow-hidden ${dark ? 'bg-slate-950 text-slate-100' : 'bg-zinc-50 text-zinc-900'}`}
        >
          <header className={`flex min-h-[72px] shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3 md:px-6 ${dark ? 'border-slate-800 bg-[#0b0f19]' : 'border-zinc-200 bg-white'}`}>
            <div className="flex min-w-0 items-center gap-4">
              <button
                type="button"
                onClick={leaveTemplateManager}
                className={`shrink-0 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${dark ? 'border-slate-700 text-slate-400 hover:text-white' : 'border-zinc-300 text-zinc-600 hover:text-zinc-950'}`}
              >
                Back to Admin Tools
              </button>
              <span aria-hidden="true" className={`hidden h-8 w-px sm:block ${dark ? 'bg-slate-800' : 'bg-zinc-200'}`} />
              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold">Email Template Manager</h1>
                <p className={`hidden text-xs sm:block ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>Reusable templates shared across Admin outreach tools.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={startNewTemplate}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_0_14px_rgba(59,130,246,0.25)] transition-colors hover:bg-blue-500"
            >
              Create New Template
            </button>
          </header>

          <div
            ref={templateColumnsRef}
            data-testid="email-template-columns"
            className={`grid min-h-0 min-w-0 flex-1 grid-cols-1 ${isWideTemplateWorkspace ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto'} ${activeColumnDivider ? 'select-none' : ''}`}
            style={isWideTemplateWorkspace ? {
              gridTemplateColumns: `minmax(14rem, ${templateColumnSizes.templates}fr) ${TEMPLATE_DIVIDER_WIDTH}px minmax(20rem, ${templateColumnSizes.editor}fr) ${TEMPLATE_DIVIDER_WIDTH}px minmax(20rem, ${templateColumnSizes.preview}fr)`,
            } : undefined}
          >
            {/* Column 1: Saved Templates */}
            <aside data-template-column="templates" className={`flex min-h-0 min-w-0 flex-col overflow-hidden border-b xl:border-b-0 ${dark ? 'border-slate-800 bg-[#111827]' : 'border-zinc-200 bg-white'}`}>
              <div className={`flex h-12 shrink-0 items-center border-b px-5 ${dark ? 'border-slate-800' : 'border-zinc-200'}`}>
                <h2 className={`text-xs font-bold uppercase tracking-[0.12em] ${dark ? 'text-slate-400' : 'text-zinc-500'}`}>Saved Templates</h2>
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
                <div className="space-y-3">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      aria-pressed={templateId === tpl.id}
                      onClick={() => void loadTemplate(tpl.id)}
                      className={`relative w-full min-w-0 rounded-lg border p-4 text-left transition-colors ${templateId === tpl.id ? (dark ? 'border-blue-500 bg-[#023E0C] shadow-[0_4px_20px_rgba(0,0,0,0.35),inset_0_0_10px_rgba(59,130,246,0.08)]' : 'border-blue-500 bg-blue-50 shadow-[0_4px_20px_rgba(59,130,246,0.12)]') : (dark ? 'border-slate-800 bg-slate-950/60 hover:border-slate-600' : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300')}`}
                    >
                      <span className={`block truncate text-[17px] font-semibold ${dark ? 'text-white' : 'text-zinc-900'}`}>{tpl.name}</span>
                      <span className={`mt-1 block truncate text-xs ${dark ? 'text-white' : 'text-zinc-600'}`}>{tpl.subject}</span>
                      <span className={`mt-3 block text-[11px] ${dark ? 'text-[#DEF448]' : 'text-zinc-500'}`}>Updated {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleString() : '—'}</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => void duplicateTemplate()}
                    disabled={!templateId || busy}
                    className={`rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${dark ? 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}
                  >
                    Duplicate selected
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteTemplate()}
                    disabled={!templateId || busy}
                    className={`rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${dark ? 'border-rose-900/70 text-rose-400 hover:bg-rose-950/50' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
                  >
                    Delete template
                  </button>
                  <button
                    type="button"
                    onClick={() => void selectTemplateForComposer()}
                    disabled={!templateId}
                    className={`mt-1 rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${dark ? 'border-teal-800 bg-teal-950/20 text-teal-400 hover:bg-teal-900/40' : 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100'}`}
                  >
                    Select for Email Portal
                  </button>
                </div>
              </div>
            </aside>

            {renderTemplateColumnDivider(1, 'Resize saved templates and editor columns')}

            {/* Column 2: Template Editor */}
            <section data-template-column="editor" className={`flex min-h-0 min-w-0 flex-col overflow-hidden border-b xl:border-b-0 ${dark ? 'border-slate-800 bg-[#131b2c]' : 'border-zinc-200 bg-zinc-50'}`} aria-label="Template editor">
              <div className={`flex h-12 shrink-0 items-center justify-between gap-3 border-b px-5 ${dark ? 'border-slate-800' : 'border-zinc-200'}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <h2 className={`shrink-0 text-xs font-bold uppercase tracking-[0.12em] ${dark ? 'text-slate-400' : 'text-zinc-500'}`}>Template Editor</h2>
                  {templateId && !templateNameEditing ? (
                    <>
                      <span className={`truncate text-xs font-semibold ${dark ? 'text-slate-200' : 'text-zinc-800'}`}>{templateForm.name}</span>
                      <button type="button" onClick={() => setTemplateNameEditing(true)} className={`text-[11px] underline ${dark ? 'text-blue-300' : 'text-blue-700'}`}>Rename</button>
                    </>
                  ) : null}
                </div>
                {templateId ? (
                  <span className={`truncate text-[11px] ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>
                    Created {editingTemplateSummary?.createdAt ? new Date(editingTemplateSummary.createdAt).toLocaleString() : '—'} · Updated {editingTemplateSummary?.updatedAt ? new Date(editingTemplateSummary.updatedAt).toLocaleString() : '—'}
                  </span>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
                {!templateId || templateNameEditing ? (
                  <label className={`block text-[13px] font-semibold ${dark ? 'text-slate-200' : 'text-zinc-800'}`}>
                    Template name
                    <input value={templateForm.name} onChange={(e) => setFormValue('name', e.target.value)} maxLength={120} className={`mt-2 w-full rounded-md border px-3 py-3 text-[13px] outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-100' : 'border-zinc-300 bg-white text-zinc-900'}`} />
                  </label>
                ) : null}
                <label className={`block text-[13px] font-semibold ${dark ? 'text-[#26E007]' : 'text-zinc-800'}`}>
                  Email subject
                  <input value={templateForm.subject} onChange={(e) => setFormValue('subject', e.target.value)} maxLength={250} className={`mt-2 w-full rounded-md border px-3 py-3 text-[13px] outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-100' : 'border-zinc-300 bg-white text-zinc-900'}`} />
                </label>
                <label className={`block text-[13px] font-semibold ${dark ? 'text-[#26E007]' : 'text-zinc-800'}`}>
                  Partnership evaluation workflow
                  <select value={templateForm.evaluationType || ''} onChange={(e) => setTemplateForm((curr) => ({ ...curr, evaluationType: (e.target.value || null) as TemplateInput['evaluationType'] }))} className={`mt-2 w-full rounded-md border px-3 py-3 text-[13px] outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-100' : 'border-zinc-300 bg-white text-zinc-900'}`}>
                    <option value="">No partnership evaluation</option>
                    <option value="WARRIOR_TRADING_EVALUATION">Warrior Trading evaluation</option>
                    <option value="GENERIC_PARTNERSHIP_EVALUATION">Generic Trade-Proof evaluation</option>
                  </select>
                  <span className={`mt-1 block text-[11px] font-normal ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>Generic Trade-Proof evaluation is for prospective contacts, including people not yet approved as White-Label tenants. It sends a recipient-bound 30-day invitation and does not add or approve a tenant. Preview does not create an invitation.</span>
                </label>
                <label className={`block text-[13px] font-semibold ${dark ? 'text-[#26E007]' : 'text-zinc-800'}`}>
                  Default personal message
                  <textarea value={templateForm.defaultPersonalMessage} onChange={(e) => setFormValue('defaultPersonalMessage', e.target.value)} maxLength={3000} rows={3} className={`mt-2 w-full resize-y rounded-md border px-3 py-3 text-[13px] leading-6 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-100' : 'border-zinc-300 bg-white text-zinc-900'}`} />
                  <span className={`mt-1 block text-[11px] font-normal ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>Use **text** for blue bold formatting. Line breaks are preserved; blank lines create paragraph spacing.</span>
                </label>
                <div>
                  <button type="button" aria-expanded={htmlEditorExpanded} aria-controls="email-portal-template-html-editor" onClick={() => setHtmlEditorExpanded((exp) => !exp)} className={`flex w-full items-center justify-between text-left text-[13px] font-semibold ${dark ? 'text-[#26E007]' : 'text-zinc-800'}`}>
                    <span>HTML editor</span>
                    <span aria-hidden="true" className="text-xs">{htmlEditorExpanded ? '▾' : '▸'}</span>
                  </button>
                  {htmlEditorExpanded ? (
                    <textarea id="email-portal-template-html-editor" aria-label="Template HTML content" value={templateForm.htmlContent} onChange={(e) => setFormValue('htmlContent', e.target.value)} spellCheck={false} rows={15} className={`mt-2 w-full resize-y rounded-md border px-3 py-3 font-mono text-xs leading-6 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#05070a] text-slate-200' : 'border-zinc-300 bg-white text-zinc-800'}`} />
                  ) : (
                    <div id="email-portal-template-html-editor" className="sr-only">HTML editor collapsed</div>
                  )}
                </div>
                <p className={`-mt-3 text-xs leading-5 ${dark ? 'text-slate-500' : 'text-zinc-500'}`}>
                  Supported placeholders: {'{{first_name}}'}, {'{{personal_message}}'}, {'{{invitation_url}}'}, {'{{CTA_URL}}'}.
                </p>
                <div>
                  <button type="button" aria-expanded={plainTextExpanded} aria-controls="email-portal-template-plain-text" onClick={() => setPlainTextExpanded((exp) => !exp)} className={`flex w-full items-center justify-between text-left text-[13px] font-semibold ${dark ? 'text-[#26E007]' : 'text-zinc-800'}`}>
                    <span>Plain-text fallback</span>
                    <span aria-hidden="true" className="text-xs">{plainTextExpanded ? '▾' : '▸'}</span>
                  </button>
                  {plainTextExpanded ? (
                    <textarea id="email-portal-template-plain-text" aria-label="Plain-text fallback" value={templateForm.textContent} onChange={(e) => setFormValue('textContent', e.target.value)} rows={6} className={`mt-2 w-full resize-y rounded-md border px-3 py-3 font-mono text-xs leading-6 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${dark ? 'border-slate-800 bg-[#05070a] text-slate-200' : 'border-zinc-300 bg-white text-zinc-800'}`} />
                  ) : (
                    <div id="email-portal-template-plain-text" className="sr-only">Plain-text fallback collapsed</div>
                  )}
                </div>
              </div>
              <div className={`sticky bottom-0 flex shrink-0 flex-wrap gap-2 border-t px-5 py-4 backdrop-blur ${dark ? 'border-slate-800 bg-[#131b2c]/95' : 'border-zinc-200 bg-zinc-50/95'}`}>
                <button type="button" onClick={() => void saveTemplate()} disabled={busy} className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50">Save Changes</button>
                <button type="button" onClick={() => void previewTemplateDraft()} disabled={busy} className={`rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${dark ? 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}>Refresh Preview</button>
              </div>
            </section>

            {renderTemplateColumnDivider(2, 'Resize editor and preview columns')}

            {/* Column 3: Live Preview */}
            <section data-template-column="preview" className={`flex min-h-[30rem] min-w-0 flex-col overflow-hidden ${dark ? 'bg-[#111827]' : 'bg-zinc-100'}`} aria-label="Template preview">
              <div className={`flex h-12 shrink-0 items-center justify-between gap-3 border-b px-5 ${dark ? 'border-slate-800' : 'border-zinc-200'}`}>
                <h2 className={`text-xs font-bold uppercase tracking-[0.12em] ${dark ? 'text-slate-400' : 'text-zinc-500'}`}>Live Email Preview</h2>
                <div className={`flex rounded-md border p-1 ${dark ? 'border-slate-800 bg-[#0b0f19]' : 'border-zinc-300 bg-white'}`}>
                  <button type="button" onClick={() => setPreviewWidth('desktop')} aria-pressed={previewWidth === 'desktop'} className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${previewWidth === 'desktop' ? (dark ? 'bg-slate-800 text-white' : 'bg-blue-600 text-white') : (dark ? 'text-slate-400 hover:text-white' : 'text-zinc-600 hover:text-zinc-950')}`}>Desktop</button>
                  <button type="button" onClick={() => setPreviewWidth('mobile')} aria-pressed={previewWidth === 'mobile'} className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${previewWidth === 'mobile' ? (dark ? 'bg-slate-800 text-white' : 'bg-blue-600 text-white') : (dark ? 'text-slate-400 hover:text-white' : 'text-zinc-600 hover:text-zinc-950')}`}>Mobile</button>
                </div>
              </div>
              <div className={`min-h-0 flex-1 px-4 py-4 ${templatePreview && !templatePreviewLoading ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'}`}>
                {templatePreviewLoading ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`flex h-64 flex-col items-center justify-center gap-3 rounded-lg border p-5 text-sm ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-300' : 'border-zinc-300 bg-white text-zinc-700'}`}
                  >
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    <span>Generating live preview…</span>
                  </div>
                ) : templatePreview ? (
                  renderPreview(templatePreview, 'Template draft preview', false, true)
                ) : (
                  <div className={`rounded-lg border p-5 text-sm ${dark ? 'border-slate-800 bg-[#0b0f19] text-slate-500' : 'border-zinc-300 bg-white text-zinc-500'}`}>
                    Preview the selected template to inspect its rendered subject, personalized HTML, and text fallback.
                  </div>
                )}
              </div>
            </section>
          </div>

          {templateError ? <p role="alert" className="mx-4 mb-3 shrink-0 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-500">{templateError}</p> : null}
          {templateNotice ? <p role="status" className="mx-4 mb-3 shrink-0 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-600">{templateNotice}</p> : null}
        </main>
      ) : null}
    </section>
  );
}
